'use client';

import {type AmenityCategory} from '#/cms/schemas/amenity-category.schema';
import {type CategoryClass} from '#/cms/schemas/category-class.schema';
import {type EventCategory} from '#/cms/schemas/event-category.schema';
import {type Event} from '#/cms/schemas/event.schema';
import {type PlaceSummary} from '#/cms/schemas/place-summary.schema';
import useAnalytics, {AnalyticsEventAction, AnalyticsEventCategory} from '#/features/analytics/useAnalytics';
import {CategoryBox} from '#/features/components/CategoryBox';
import {PlaceFilter} from '#/features/components/PlaceFilter';
import {Heading2, SmallText} from '#/features/components/Text';
import TextSearchBox from '#/features/components/TextSearchBox';
import {usePlaceStore} from '#/features/store/placeStore';
import {useUiStrings} from '#/features/store/uiStrings';
import {Radius, type RadiusOption} from '#/utils/geo';
import {
  AMENITIES_SEARCH_PARAM_KEY,
  CATEGORIES_SEARCH_PARAM_KEY,
  PANEL_VIEW_SEARCH_PARAM_KEY,
  PLACE_SEARCH_PARAM_KEY,
  RADIUS_SEARCH_PARAM_KEY,
  useActiveFilters,
  useClearQueryParams,
  useURLFilterParams,
  useUpdateQueryParams,
} from '#/utils/queryParams';
import updateActiveFilters from '#/utils/updateActiveFilters';
import classNames from 'classnames';
import {type Dayjs} from 'dayjs';
import {useCallback, useMemo, useState, type ComponentProps, useEffect} from 'react';
import CalendarFilter from './CalendarFilter';
import {
  useCategoryFilterLogic,
  useDisabledAmenityCategoryIdsForEvents,
  useDisabledEventCategoryIdsForEvents,
} from '#/utils/categoryHooks';

/**
 * Whether to show all places in the place filter or only places that have events.
 */

type Props = ComponentProps<'div'> & {
  events: Event[];
  places: PlaceSummary[];
  categoryClasses: CategoryClass[];
  eventCategories: EventCategory[];
  amenityCategories: AmenityCategory[];
  filteredEvents: Event[];
  searchText: string;
  handleSearchTextChange: (searchText: string | null) => void;
  unMountInputs?: boolean;
  placeFilterPlacesType?: 'allPlaces' | 'eventPlaces';
};

export const EventsFilter = (props: Props) => {
  const {
    className,
    events,
    places,
    categoryClasses,
    eventCategories,
    amenityCategories,
    filteredEvents,
    searchText,
    handleSearchTextChange,
    unMountInputs = false,
    placeFilterPlacesType = 'allPlaces',
    ...otherProps
  } = props;
  const activeFilters = useActiveFilters(eventCategories, amenityCategories);

  const {registerEvent} = useAnalytics();
  const {placeMap} = usePlaceStore();
  const categoryFilterLogic = useCategoryFilterLogic();
  const urlFilterParams = useURLFilterParams();
  const updateQueryParams = useUpdateQueryParams();
  const clearQueryParams = useClearQueryParams();
  const [startDate, setStartDate] = useState<Dayjs | null>(urlFilterParams.startDate);
  const [endDate, setEndDate] = useState<Dayjs | null>(urlFilterParams.endDate);
  const uiStrings = useUiStrings();

  const disabledEventCategoryIds = useDisabledEventCategoryIdsForEvents({
    eventCategories,
    activeEventCategoryIds: urlFilterParams.categories,
    filteredEvents,
    eventCategoryFilterLogic: categoryFilterLogic.eventCategories,
  });

  const disabledAmenityCategoryIds = useDisabledAmenityCategoryIdsForEvents({
    amenityCategories,
    activeAmenityCategoryIds: urlFilterParams.amenities,
    filteredEvents,
    amenityCategoryFilterLogic: categoryFilterLogic.amenityCategories,
  });

  const handleClearAll = useCallback(() => {
    clearQueryParams({blacklistKeys: [PANEL_VIEW_SEARCH_PARAM_KEY]});
  }, [clearQueryParams]);

  useEffect(() => {
    setStartDate(urlFilterParams.startDate);
    setEndDate(urlFilterParams.endDate);
  }, [urlFilterParams.startDate, urlFilterParams.endDate]);

  useEffect(() => {
    handleSearchTextChange(urlFilterParams.text);
  }, [handleSearchTextChange, urlFilterParams.text]);

  const handleDateRangeChange = useCallback(
    (label: string, newStartDate: Dayjs | null, newEndDate: Dayjs | null) => {
      setStartDate(newStartDate);
      setEndDate(newEndDate);
      registerEvent({
        category: AnalyticsEventCategory.Events,
        action: AnalyticsEventAction.FilterByTime,
        label,
        startDate: !!newStartDate ? newStartDate.toISOString() : '',
        endDate: !!newEndDate ? newEndDate.toISOString() : '',
      });
    },
    [registerEvent]
  );

  const handlePlaceSelect = useCallback(
    (placeId: PlaceSummary['id'] | null) => {
      const radius = urlFilterParams.radius ?? Radius['5Miles'];
      updateQueryParams({
        [PLACE_SEARCH_PARAM_KEY]: placeId,
        [RADIUS_SEARCH_PARAM_KEY]: !!placeId ? radius : null,
      });
      if (placeId) {
        const place = placeMap.get(placeId);
        if (!place) throw new Error(`Place ${placeId} not found`);
        registerEvent({
          category: AnalyticsEventCategory.Events,
          action: AnalyticsEventAction.FilterByPlace,
          label: place.title ?? place.id,
          radius,
          placeId,
        });
      }
    },
    [placeMap, registerEvent, updateQueryParams, urlFilterParams.radius]
  );

  const handleRadiusSelect = useCallback(
    (radiusOption: RadiusOption) => {
      updateQueryParams({
        [RADIUS_SEARCH_PARAM_KEY]: radiusOption.id,
      });
      registerEvent({
        category: AnalyticsEventCategory.Events,
        action: AnalyticsEventAction.FilterByPlace,
        label: radiusOption.title,
        radius: radiusOption.id,
        placeId: urlFilterParams.placeId ?? '',
      });
    },
    [registerEvent, updateQueryParams, urlFilterParams.placeId]
  );

  const handleCategoryFilterChange = useCallback(
    (change: Record<string, boolean>) => {
      const activeCategories = updateActiveFilters(urlFilterParams.categories, change);
      updateQueryParams({[CATEGORIES_SEARCH_PARAM_KEY]: activeCategories});
      for (const [categoryId, active] of Object.entries(change)) {
        if (active) {
          const category = eventCategories.find(c => c.id === categoryId);
          if (!category) throw new Error(`Event category ${categoryId} not found`);
          registerEvent({
            category: AnalyticsEventCategory.Events,
            action: AnalyticsEventAction.FilterByCategory,
            label: category.title ?? category.id,
            categoryId,
          });
        }
      }
    },
    [eventCategories, registerEvent, updateQueryParams, urlFilterParams.categories]
  );

  const handleAmenityFilterChange = useCallback(
    (change: Record<string, boolean>) => {
      const activeAmenities = updateActiveFilters(urlFilterParams.amenities, change);
      updateQueryParams({[AMENITIES_SEARCH_PARAM_KEY]: activeAmenities});
      for (const [categoryId, active] of Object.entries(change)) {
        if (active) {
          const category = amenityCategories.find(c => c.id === categoryId);
          if (!category) throw new Error(`Amenity category ${categoryId} not found`);
          registerEvent({
            category: AnalyticsEventCategory.Events,
            action: AnalyticsEventAction.FilterByAmenity,
            label: category.title ?? category.id,
            categoryId,
          });
        }
      }
    },
    [amenityCategories, registerEvent, updateQueryParams, urlFilterParams.amenities]
  );

  const eventPlaces = useMemo(() => {
    const result = new Set<PlaceSummary['id']>();
    for (const event of events) {
      for (const placeId of event.placeIds ?? []) {
        result.add(placeId);
      }
    }
    return [...result];
  }, [events]);

  const allPlaces = useMemo(() => {
    const result = new Set<PlaceSummary['id']>();
    for (const place of places) {
      result.add(place.id);
    }
    return [...result];
  }, [places]);

  const eventCategoryClasses = useMemo(
    () =>
      categoryClasses.filter(categoryClass =>
        eventCategories.some(category => !!category.class && category.class === categoryClass.id)
      ),
    [categoryClasses, eventCategories]
  );

  const amenityCategoryClasses = useMemo(
    () =>
      categoryClasses.filter(categoryClass =>
        amenityCategories.some(category => !!category.class && category.class === categoryClass.id)
      ),
    [categoryClasses, amenityCategories]
  );

  const isFilterActive = activeFilters.length !== 0;

  return (
    <div
      className={classNames('flex shrink-0 flex-col py-14 md:relative mobile:w-full mobile:py-6', className)}
      {...otherProps}
    >
      <div className="pt-X z-50 flex flex-row justify-between bg-background px-4 md:sticky md:top-0 md:w-full">
        <Heading2 id={`filter-panel-title${unMountInputs ? 'unMounted' : ''}`}>
          {uiStrings.general.filterTitle}
        </Heading2>
        <button className={classNames('desktop-only -mb-2', !isFilterActive && 'invisible')} onClick={handleClearAll}>
          <SmallText className="text-primary hover:text-secondary">{uiStrings.general.clearAll}</SmallText>
        </button>
      </div>
      <div className="sticky top-9 z-30 hidden h-[1px] w-full md:block md:bg-midground3" />
      <div className="relative -top-[1px] z-40 hidden h-[1px] w-full md:block md:bg-background" />
      <div className="z-20 flex flex-col gap-4 p-4">
        <TextSearchBox
          className="desktop-only"
          placeholder={uiStrings.events.searchPlaceholder}
          text={searchText}
          onTextChange={handleSearchTextChange}
          id={'events-search'}
          unMountInput={unMountInputs}
        />
        <CalendarFilter
          tooltipWithEmphasis={uiStrings.events.tooltipWithEmphasisCalendarFilter}
          startDate={startDate}
          endDate={endDate}
          onChangeTimeRange={handleDateRangeChange}
          unMountInputs={unMountInputs}
        />
        <PlaceFilter
          tooltipWithEmphasis={uiStrings.events.tooltipWithEmphasisPlaceFilter}
          availablePlaces={placeFilterPlacesType === 'eventPlaces' ? eventPlaces : allPlaces}
          selectedPlace={urlFilterParams.placeId}
          selectedRadius={urlFilterParams.radius}
          onSelectPlace={handlePlaceSelect}
          onSelectRadius={handleRadiusSelect}
          unMountInputs={unMountInputs}
        />
        <CategoryBox<EventCategory>
          title={uiStrings.events.eventCategories}
          tooltipWithEmphasis={uiStrings.events.tooltipWithEmphasisEventCategoryFilter}
          categoryClasses={eventCategoryClasses}
          categories={eventCategories}
          activeCategoryIds={urlFilterParams.categories}
          disabledCategoryIds={disabledEventCategoryIds}
          onFilterChange={handleCategoryFilterChange}
        />
        <CategoryBox<AmenityCategory>
          title={uiStrings.amenities.title}
          tooltipWithEmphasis={uiStrings.events.tooltipWithEmphasisAmenityCategoryFilter}
          categoryClasses={amenityCategoryClasses}
          categories={amenityCategories}
          activeCategoryIds={urlFilterParams.amenities}
          disabledCategoryIds={disabledAmenityCategoryIds}
          onFilterChange={handleAmenityFilterChange}
        />
      </div>
    </div>
  );
};
